import { I18nService } from "../i18n/types"
import { formatAnyAsHtml } from "../utils/format"

/// <reference path="../utils/format.ts" />

export function defineTextFormatDirectives(module: ng.IModule) {

    /** om-text-format: apply basic text formating (chemical compounds, **{text}** and __{text}__ markdown constructs) */
    module.directive("omTextFormat", textFormat)

    module.filter("omtext", textFilter)

    /** om-text: translate static text */
    module.directive("omText", textDirective)

    /** om-text-tmpl: translate dynamic text */
    module.directive("omTextTmpl", textTmplDirective)

    /** om-placeholder: translate placeholder attributes */
    module.directive("omPlaceholder", getTranslateAttribute("placeholder"))

    /** om-tite: translate title attributes */
    module.directive("omTitle", getTranslateAttribute("title"))

}


const textFilter = ["OmniaI18n", (i18n: I18nService) => (input: string) => {

    if (!input) {
        return input
    }

    const { msg, found } = i18n.getMessage(input)

    return found
        ? msg
        : `(?${input})`

}]


function getTranslateAttribute(attr: string) {

    function get(i18n: I18nService): ng.IDirective {

        return {

            restrict: "A",

            compile: (element, attrs) => {

                const e = element[0]

                let n = attr.replace(/^-([^-])/g, g => g[1].toUpperCase())
                n = "om" + n[0].toUpperCase() + n.substring(1)
                const a = attrs[n]

                const { msg, found } = i18n.getMessage(a)

                const text = found ? msg : ("(?" + a + ")")

                e.removeAttribute(a)
                e.setAttribute(attr, text)
            }

        }

    }

    return ["OmniaI18n", get]


}


const textDirective = ["$sce", "OmniaI18n", text]

/**
 * Implementation of the om-text directive
 *
 * This directive is used to translate static text
 * It looks for the single non-whitespace child text node for the directive's element,
 * and replaces its text using the "OmniaI18n" service
 */
function text($sce: ng.ISCEService, i18n: I18nService): ng.IDirective {

    return {

        restrict: "A",

        compile: (element: ng.IAugmentedJQuery, attrs: ng.IAttributes) => {

            const debug = Boolean(getAttribute(attrs, "debug"))

            const setText = getSetText($sce, i18n, attrs, debug)

            const target = findTargetTextNode(element[0])

            if (target) {
                const { text, node, prefix, suffix } = target
                setText(text, node, prefix, suffix)
            }

        }

    }

}

const textTmplDirective = ["$sce", "OmniaI18n", textTmpl]

/**
 * Implementation of the om-text-tmpl directive
 *
 * This directive is used to translate dynamic text
 * It looks for the single non-whitespace child text node for the directive's element,
 * and checks that the text is a template expression (e.g., {{vm.property}})
 * If it is, it returns a link function which will watch this expression,
 * and replace it with its translated version.
 */
function textTmpl($sce: ng.ISCEService, i18n: I18nService): ng.IDirective {

    return {

        restrict: "A",

        compile: (element: ng.IAugmentedJQuery, attrs: ng.IAttributes) => {

            const debug = Boolean(getAttribute(attrs, "debug"))

            const setText = getSetText($sce, i18n, attrs, debug)

            const { binding, asAttribute: targetSelf, text, node } = getBinding(element, attrs)

            const log = (...args) => {
                if (debug) console.log(...args)
            }

            log("text-tmpl.compile", { binding, targetSelf, text, node })

            if (!binding) {

                if (node) {
                    // Not a single binding, so we assume that this is static text to be translated
                    setText(text, node)
                }

                return

            }

            // If the inner text is a single binding (e.g., {{ vm.property }}),
            // then we watch the value of this binding, and translate it each time it changes

            return (scope: ng.IScope, element: ng.IAugmentedJQuery) => {

                scope.$watch<any>(binding, v => {

                    const e = element[0]

                    const target = targetSelf
                        ? e
                        : findTargetTextNode(e)?.node

                    log("text-tmpl.watch", { binding, target, v })

                    if (target) {
                        v = (v == undefined) ? binding : v
                        setText(v, target)
                    }

                })

            }

        }

    }

    function getBinding(element: ng.IAugmentedJQuery, attrs: ng.IAttributes) {

        const binding = getAttribute(attrs, "omTextTmpl")

        if (binding) {
            return { binding, asAttribute: true }
        }

        const target = findTargetTextNode(element[0])

        if (target) {

            const { text, node } = target

            const binding = isAngularBinding(text)

            return { binding, text, node }

        }

        return {}

    }

}

const getSetText = ($sce: ng.ISCEService, i18n: I18nService, attrs: ng.IAttributes, debug = false) => (text: string, node: ChildNode, prefix?: string, suffix?: string) => {


    const highlightIfOk = true

    const html = getAttribute(attrs, "omHtml", "1")

    const { msg, found } = i18n.getMessage(text)

    if (!found) {

        if (debug) {
            console.log("i18n message not found", text)
        }

        const notfound = node.ownerDocument.createElement("span")
        notfound.classList.add(textTargetAttr)
        notfound.classList.add("omt-notfound")

        if (html) {
            notfound.innerHTML = (msg ?? text)
        }
        else {
            notfound.textContent = "(?" + (msg ?? text) + ")"
        }

        node.replaceWith(notfound)

        return

    }

    const getFullText = () => (prefix ?? "") + msg + (suffix ?? "")

    if (html || highlightIfOk) {

        const wrapper = node.ownerDocument.createElement("span")

        wrapper.classList.add(textTargetAttr)
        wrapper.classList.add("omt-ok")

        if (html) {
            wrapper.innerHTML = $sce.trustAsHtml(msg)
        }
        else {
            wrapper.textContent = getFullText()
        }

        node.replaceWith(wrapper)

    }

    else {

        node.replaceWith(getFullText())

    }

}



const textTargetAttr = "omt"

/**
 * Returns the single non-whitespace text child node of an element, if any
 */
function findTargetTextNode(element: HTMLElement) {

    const nodes = Array.from(element.childNodes)
        .filter(e => (e.nodeType == Node.TEXT_NODE) || ((e instanceof Element) && e.classList.contains(textTargetAttr)))
        .map(e => ({
            ...trim(e.textContent),
            node: e
        }))
        .filter(e => Boolean(e.text))

    return (nodes.length === 1)
        ? nodes[0]
        : undefined

}

/**
 * If "text" is a single Angular binding, i.e., a single expression enclosed in {{[expr]}},
 * then returns this expression
 */
function isAngularBinding(text: string) {
    const m = /^\s*\{\{\s*(.+)\s*\}\}\s*$/.exec(text)
    return m ? m[1] : undefined
}


function getAttribute(attrs: ng.IAttributes, name: string, defaultValue?: string): string {

    const v = attrs[name]

    return v === undefined
        ? undefined
        : v === ""
            ? defaultValue
            : v

}

function textFormat(): ng.IDirective {

    return {

        restrict: "A",

        compile: (element: ng.IAugmentedJQuery, attrs: ng.IAttributes) => {

            const e = element[0]
            const t = formatAnyAsHtml(e.innerHTML)

            e.innerHTML = t

        },

    }

}

function trim(text: string) {

    const m = text
        ? /^(\s*)(.+)/.exec(text)
        : null

    if (!m) {
        return { text: "" }
    }

    const prefix = m[1] ?? ""
    const f = m[2] ?? ""

    text = f.trim()

    const suffix = (text.length < f.length)
        ? f.substring(text.length)
        : ""

    return {
        text,
        prefix,
        suffix
    }

}
